import { makeAutoObservable, runInAction } from "mobx";
import { IMerchantOnboarding } from "../onboarding-types";
import OnboardingDataStore, {
    IGetMerchantOnboardingModel,
    IOnboardingScreenStore
} from "./onboarding-data-store";
import { PlanStore } from "../../subscription-plans/stores/subscription-plans-store";
import SubscriptionPlansService, { IPlanResponse } from "../../../services/api/subscription-plans-service";
import { rootContainer, TYPE } from "../../../services/container/root";
import { IIframeService } from "../../../services/iframe-service/iframe-service";
import onboardingPlansStores from "./onboarding-plans-stores";
import { OnboardingNavigationStore } from "./onboarding-navigation-store";
import { isSet } from "../../../models/model-types-methods";

class ApprovePlanScreenStore implements IOnboardingScreenStore {
    private _getModel: IGetMerchantOnboardingModel;
    private _onboardingDataStore: OnboardingDataStore;
    private _is_generating_link: boolean = false;
    private _isLoading: boolean = false;

    constructor(onboardingDataStore: OnboardingDataStore, private _navigationStore: OnboardingNavigationStore) {
        this._onboardingDataStore = onboardingDataStore;
        this._getModel = this._onboardingDataStore.getModel;
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private get iframeService(): IIframeService {
        return rootContainer.get<IIframeService>(TYPE.IframService);
    }

    private get subscriptionPlansService(): SubscriptionPlansService {
        return rootContainer.get<SubscriptionPlansService>(TYPE.SubscriptionPlansService);
    }

    public get isLoading(): boolean { return this._isLoading; }

    public get isCoveragePlanSelected(): boolean {
        return this._onboardingDataStore.isSelectedCoveragePlanId();
    }
    public get shouldHideCoverage(): boolean { return this._onboardingDataStore.getSetting()?.should_hide_coverage_screens ?? false; }

    public isComplete(): boolean {
        return this._onboardingDataStore.getSetting()?.merchant?.is_plan_approved ?? false;
    }

    public get planId(): number | null | undefined {
        return this._getModel()?.plan_id;
    }

    public getUpdatedModel(): IMerchantOnboarding {
        return {...this._getModel() } as IMerchantOnboarding;
    }

    public getMixPanelData(): Record<string, any> {
        return {};
    }

    public hasChanges(): boolean {
        return false;
    }

    public discardChanges(): void {}

    public approvePlan(): void {
        const planId = this.planId;
        if (!planId) {
            console.error("[onboarding wizard] should not happen - no planId in approve plan screen");
            return;
        }

        /**
         * If a User has NOT chosen another plan, just open the next Wizard step.
         */
        if (this.isSelectedPlanApproved()) {
            this._navigationStore.nextStep();
            return;
        }
        
        const plan = onboardingPlansStores.getPlanById(planId);
        if (!plan) {
            return;
        }
        this.generateApprovalBillingLink(plan);
    }

    /**
     * Returns true if the selected plan is approved and unchanged, indicating we should skip to the next step.
     */
    private isSelectedPlanApproved(): boolean {
        const onboarding = this._getModel();

        if (!isSet(onboarding)) {
            return false;
        }

        const onboardingSystemPlan = onboarding?.system_plan;
        const userPlanId = onboarding?.shop?.plan_id;

        const isOnboardingPlanApproved = onboardingSystemPlan?.id === userPlanId;
        const isOnboardingPlanUnchanged = onboardingSystemPlan?.parent_plan_id === this.planId;

        return Boolean(userPlanId && isOnboardingPlanApproved && isOnboardingPlanUnchanged);
    }

    public previousStep(): void {
        this._navigationStore.previousStep();
    }

    private async generateApprovalBillingLink(newPlan: PlanStore) {
        /** this._is_generating_link prevents from posting another request while being processing */
        if (this._is_generating_link) {
            return false;
        }
        this._is_generating_link = true;
        this._isLoading = true;

        if (newPlan) {
            let generateBillingLink: IPlanResponse|undefined;

            try {
                generateBillingLink = await this.subscriptionPlansService.generateApprovalBillingLink({
                    onboarding: this._getModel() as IMerchantOnboarding,
                    plan: newPlan.getModel()
                });
            } 
            catch { }

            runInAction(() => {
                if (generateBillingLink?.redirect_to) {
                    this.iframeService.redirectParentFrame(generateBillingLink.redirect_to);
                } 
                this._is_generating_link = false;
                this._isLoading = false;
            });
        } 
        else {
            this._is_generating_link = false;
            this._isLoading = false;
        }
    }

}

export default ApprovePlanScreenStore;